import { Injectable, OnDestroy, NgZone } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { ZikataToastService, ZikataToast } from '@zikata-frontend/ui';

const WARN_BEFORE_SECONDS = 30;

@Injectable({ providedIn: 'root' })
export class TokenExpiryService implements OnDestroy {
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private countdownToast: ZikataToast | null = null;
  private warningActive = false;
  private lastWarningSecond = -1; // Track the last second we showed
 
  constructor(
    private keycloakService: KeycloakService,
    private toastService: ZikataToastService,
    private ngZone: NgZone,
  ) {}
 
  startWatching(): void {
    if (this.pollTimer) return;
    this.ngZone.runOutsideAngular(() => {
      this.pollTimer = setInterval(() => this.tick(), 1000);
    });
  }
 
  stopWatching(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    this.dismissCountdownToast();
    this.warningActive = false;
    this.lastWarningSecond = -1;
  }
 
  ngOnDestroy(): void {
    this.stopWatching();
  }
 
  private tick(): void {
    const secondsLeft = this.getSecondsUntilExpiry();
    
    if (secondsLeft === null) return;
 
    const clampedSeconds = Math.max(secondsLeft, 0);

    if (clampedSeconds <= 0) {
      this.ngZone.run(() => this.handleExpired());
    } 
    else if (clampedSeconds <= WARN_BEFORE_SECONDS && clampedSeconds > 0) {
      if (Math.floor(clampedSeconds) !== this.lastWarningSecond) {
        this.lastWarningSecond = Math.floor(clampedSeconds);
        this.ngZone.run(() => this.showOrUpdateCountdown(Math.floor(clampedSeconds)));
      }
    } 
    else if (this.warningActive && clampedSeconds > WARN_BEFORE_SECONDS) {
      this.ngZone.run(() => {
        this.dismissCountdownToast();
        this.warningActive = false;
        this.lastWarningSecond = -1;
      });
    }
  }
  
  private getSecondsUntilExpiry(): number | null {
    try {
      const exp = this.keycloakService.getKeycloakInstance()?.tokenParsed?.exp;
      
      if (exp === null || exp === undefined || typeof exp !== 'number') {
        return null;
      }
      
      const secondsLeft = exp - Math.floor(Date.now() / 1000);
      
      return secondsLeft > 0 ? secondsLeft : 0;
    } catch {
      return null;
    }
  }
 
 private showOrUpdateCountdown(secondsLeft: number): void {
  const message = `Session expiring in ${secondsLeft}s — you will be logged out automatically.`;

  if (!this.warningActive) {
    this.countdownToast = {
      text: message,
      classname: 'bg-warning text-dark',
      delay: 0,
      sticky: true,
    };
    this.toastService.toasts.push(this.countdownToast);
    this.warningActive = true;
  } else if (this.countdownToast) {
    // ✅ Mutate the existing object in-place — no array reassignment, no blink
    this.countdownToast.text = message;
  }
}
 
  private dismissCountdownToast(): void {
    if (this.countdownToast) {
      this.toastService.remove(this.countdownToast);
      this.countdownToast = null;
      console.log('[TokenExpiry] Warning dismissed');
    }
  }
 
  private handleExpired(): void {
    console.log('[TokenExpiry] Token expired — initiating logout');
    this.stopWatching();
    this.toastService.showDanger('Your session has expired. Logging you out…');
 
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.ngZone.run(() => {
          console.log('[TokenExpiry] Calling keycloakService.logout()');
          this.keycloakService.logout(window.location.origin).catch((err) => {
            console.error('[TokenExpiry] Logout failed:', err);
            window.location.href = window.location.origin;
          });
        });
      }, 1500);
    });
  }
}